/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unnecessary-type-assertion */
/**
 * Model probe orchestrator. Loads a GLB/glTF file and extracts geometry,
 * texture, and material statistics.
 */

import { readDeclaredExtensions, readModel } from './modelIO';
import type {
  AlphaAnalysis,
  MaterialInfo,
  MaterialTextureSlot,
  ModelProbeReport,
  RootTransformInfo,
  TextureInfo,
} from './types';

export interface ProbeOptions {
  /** Called with warnings as they occur. */
  onWarning?: (warning: { code: string; message: string; detail?: string }) => void;
}

/**
 * Base64-encode a byte array via a chunked loop. `btoa(String.fromCharCode(...
 * bytes))` overflows the engine's max argument count (~64–125K) for anything
 * but tiny inputs, so we build the binary string in fixed-size chunks instead.
 */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000; // 32K — safely under the spread/arg-count limit.
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/**
 * Sample an image's alpha channel to classify how transparency is used.
 * Downsamples to a 128px grid so the cost is bounded regardless of texture size.
 * Returns hasAlpha=false for images whose alpha is entirely opaque (a wasted
 * channel that should be dropped).
 */
function analyzeAlpha(bitmap: ImageBitmap): { hasAlpha: boolean | undefined; analysis?: AlphaAnalysis } {
  const SAMPLE = 128;
  const scale = Math.min(1, SAMPLE / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = new OffscreenCanvas(w, h);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return { hasAlpha: undefined };
  // Nearest-neighbor downsample: bilinear smoothing would synthesize partial
  // alpha at hard cutout edges and misclassify a MASK texture as a soft BLEND.
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(bitmap, 0, 0, w, h);
  const data = ctx.getImageData(0, 0, w, h).data;

  const total = w * h;
  let opaque = 0;
  let transparent = 0;
  let partial = 0;
  const OPAQUE_T = 250; // ≥ ~0.98
  const TRANSP_T = 5; // ≤ ~0.02
  for (let i = 0; i < total; i++) {
    const a = data[i * 4 + 3]!;
    if (a >= OPAQUE_T) opaque++;
    else if (a <= TRANSP_T) transparent++;
    else partial++;
  }

  const opaqueFraction = opaque / total;
  const transparentFraction = transparent / total;
  const partialFraction = partial / total;

  // Effectively no transparency → the alpha channel is wasted.
  if (transparentFraction < 0.001 && partialFraction < 0.005) {
    return {
      hasAlpha: true,
      analysis: { usage: 'opaque', opaqueFraction, transparentFraction, partialFraction },
    };
  }
  // Hard-edged cutout (foliage, decals): mostly 0/255, little soft band.
  if (partialFraction < 0.03) {
    return {
      hasAlpha: true,
      analysis: { usage: 'binary', opaqueFraction, transparentFraction, partialFraction },
    };
  }
  // Genuine partial transparency.
  return {
    hasAlpha: true,
    analysis: { usage: 'smooth', opaqueFraction, transparentFraction, partialFraction },
  };
}

/**
 * Derive actionable hints for a material from its alpha mode and the measured
 * alpha usage of its base-color texture. The recommendations mirror real
 * authoring mistakes: BLEND on opaque content, BLEND on hard cutouts that
 * should be MASK, and PNGs that could be JPEGs once alpha is dropped.
 */
function analyzeMaterial(
  material: MaterialInfo,
  textures: TextureInfo[],
): { hints: MaterialInfo['hints']; recommended: MaterialInfo['recommended'] } {
  const hints: MaterialInfo['hints'] = [];
  const recommended: MaterialInfo['recommended'] = {};
  const baseRef = material.textures.find((t) => t.slot === 'baseColor');
  const baseTex = baseRef ? textures[baseRef.textureIndex] : undefined;
  const usage = baseTex?.alpha?.usage;

  if (material.alphaMode === 'BLEND') {
    if (usage === 'opaque' || usage === 'none' || (baseTex && baseTex.hasAlpha === false)) {
      recommended.alphaMode = 'OPAQUE';
      hints.push({
        severity: 'warn',
        title: 'Switch BLEND → OPAQUE',
        detail:
          'Base-color texture has no real transparency, but the material blends. OPAQUE renders faster, sorts correctly, and lets the texture compress as JPEG.',
      });
    } else if (usage === 'binary') {
      recommended.alphaMode = 'MASK';
      hints.push({
        severity: 'warn',
        title: 'Switch BLEND → MASK (alpha clip)',
        detail:
          'Alpha is hard-edged (cutout) with little partial transparency. MASK avoids blend sorting artifacts and is cheaper than BLEND for foliage/decals.',
      });
    }
  }

  if (material.alphaMode === 'MASK' && usage === 'smooth') {
    recommended.alphaMode = 'BLEND';
    hints.push({
      severity: 'info',
      title: 'Consider BLEND for soft edges',
      detail:
        'This texture has significant partial alpha, which alpha-clip (MASK) will harden into jagged edges. BLEND preserves the soft transition.',
    });
  }

  // PNG that could be JPEG once alpha is unused.
  if (baseTex && baseTex.mimeType === 'image/png' && (usage === 'opaque' || baseTex.hasAlpha === false)) {
    recommended.baseColorFormat = 'jpeg';
    hints.push({
      severity: 'info',
      title: 'Convert base color PNG → JPEG',
      detail:
        'No alpha is needed here. JPEG is typically 5–10× smaller than PNG for photographic color maps.',
    });
  }

  // Only suggest dropping double-sided for a watertight solid. An open shell
  // (sunroof, glass, cloth) genuinely needs both faces drawn — disabling it
  // would make the back faces vanish.
  if (material.doubleSided && usage === 'opaque' && material.openShell === false) {
    recommended.doubleSided = false;
    hints.push({
      severity: 'info',
      title: 'Disable double-sided',
      detail:
        'This is a closed solid, so back-faces are never visible. Single-sided halves the fragment work for this material.',
    });
  }

  return { hints, recommended };
}

/**
 * Is a material's geometry an "open shell"? A watertight solid has every edge
 * shared by exactly two triangles (0 boundary edges); a thin single-layer
 * surface (sunroof, glass, cloth, leaves) has many boundary edges. We sum the
 * boundary-edge ratio across all primitives using the material; a meaningful
 * fraction of boundary edges means the back faces are visible, so double-sided
 * is genuinely needed and we must NOT recommend disabling it.
 */
function isMaterialOpenShell(root: any, material: unknown): boolean {
  let boundary = 0;
  let total = 0;
  for (const mesh of root.listMeshes()) {
    for (const prim of mesh.listPrimitives()) {
      if (prim.getMaterial() !== material) continue;
      const indices = prim.getIndices();
      // Skip non-indexed primitives: every triangle owns its three vertices, so
      // no two triangles share an edge by index and the boundary ratio would be
      // a meaningless 1.0. Excluding them conservatively suppresses the
      // double-sided hint rather than forcing it on every unindexed mesh.
      if (!indices) continue;
      const arr = indices.getArray() as Uint16Array | Uint32Array;
      // Count how many triangles reference each undirected edge.
      const edgeCount = new Map<string, number>();
      for (let i = 0; i + 2 < arr.length; i += 3) {
        const a = arr[i]!, b = arr[i + 1]!, c = arr[i + 2]!;
        for (const [u, v] of [[a, b], [b, c], [c, a]] as Array<[number, number]>) {
          const key = u < v ? `${u}_${v}` : `${v}_${u}`;
          edgeCount.set(key, (edgeCount.get(key) ?? 0) + 1);
        }
      }
      for (const count of edgeCount.values()) {
        total++;
        if (count === 1) boundary++; // edge with only one adjacent triangle
      }
    }
  }
  if (total === 0) return false;
  // >2% boundary edges → treat as an open shell. Watertight meshes sit near 0%;
  // thin surfaces are far higher (the BMW sunroof glass is ~37%).
  return boundary / total > 0.02;
}

/** Column-major 4×4 multiply (a then b → a·b), matching glTF's matrix layout. */
function mat4Multiply(a: number[], b: number[]): number[] {
  const out = new Array<number>(16).fill(0);
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      for (let k = 0; k < 4; k++) {
        out[col * 4 + row] += a[k * 4 + row]! * b[col * 4 + k]!;
      }
    }
  }
  return out;
}

const MAT4_IDENTITY: number[] = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

/**
 * World-space bounding box, accounting for node transforms.
 *
 * A primitive's POSITION accessor holds LOCAL coordinates; the node graph's
 * TRS (often a uniform scale, e.g. Unity exports a 100× "GameObject" root)
 * places it in the world. Summing raw local positions — as a naive pass does —
 * reports the model at the wrong scale (a 0.64-unit globe reads as 0.0064).
 *
 * We walk every scene's node tree, compose each node's local matrix with its
 * parent's, and transform that node's mesh positions into world space before
 * accumulating min/max. Falls back to raw local positions only if the file has
 * no scenes (a mesh-but-no-scene glTF is unusual but valid).
 */
function computeWorldBounds(
  root: any,
): [number, number, number, number, number, number] {
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

  const accumulate = (mesh: any, world: number[]): void => {
    for (const primitive of mesh.listPrimitives()) {
      const positions = primitive.getAttribute('POSITION');
      if (!positions) continue;
      const a = positions.getArray() as Float32Array;
      for (let i = 0; i + 2 < a.length; i += 3) {
        const x = a[i]!, y = a[i + 1]!, z = a[i + 2]!;
        // world (column-major) · [x, y, z, 1]
        const wx = world[0]! * x + world[4]! * y + world[8]! * z + world[12]!;
        const wy = world[1]! * x + world[5]! * y + world[9]! * z + world[13]!;
        const wz = world[2]! * x + world[6]! * y + world[10]! * z + world[14]!;
        if (wx < minX) minX = wx; if (wx > maxX) maxX = wx;
        if (wy < minY) minY = wy; if (wy > maxY) maxY = wy;
        if (wz < minZ) minZ = wz; if (wz > maxZ) maxZ = wz;
      }
    }
  };

  const walk = (node: any, parentWorld: number[]): void => {
    const world = mat4Multiply(parentWorld, node.getMatrix() as number[]);
    const mesh = node.getMesh?.();
    if (mesh) accumulate(mesh, world);
    for (const child of node.listChildren()) walk(child, world);
  };

  const scenes = root.listScenes();
  if (scenes.length > 0) {
    for (const scene of scenes) {
      for (const node of scene.listChildren()) walk(node, MAT4_IDENTITY);
    }
  } else {
    // No scene graph — fall back to untransformed local positions.
    for (const mesh of root.listMeshes()) accumulate(mesh, MAT4_IDENTITY);
  }

  if (!Number.isFinite(minX)) return [0, 0, 0, 0, 0, 0];
  return [minX, minY, minZ, maxX, maxY, maxZ];
}

/** Raw (untransformed) extent of all primitive positions — vertex-space size. */
function computeLocalExtent(root: any): [number, number, number] {
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (const mesh of root.listMeshes()) {
    for (const primitive of mesh.listPrimitives()) {
      const positions = primitive.getAttribute('POSITION');
      if (!positions) continue;
      const a = positions.getArray() as Float32Array;
      for (let i = 0; i + 2 < a.length; i += 3) {
        const x = a[i]!, y = a[i + 1]!, z = a[i + 2]!;
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
        if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
      }
    }
  }
  if (!Number.isFinite(minX)) return [0, 0, 0];
  return [maxX - minX, maxY - minY, maxZ - minZ];
}

/**
 * Summarize how scene-graph transforms scale the geometry relative to its raw
 * vertex coordinates. Rather than inspecting a single "root" node (the scaling
 * node is often deeper — e.g. Unity nests the 100× "GameObject" under an
 * identity scene child), we compare the WORLD bounds against the raw LOCAL
 * vertex extent. Their ratio is the effective scale the world size includes —
 * exactly the fact the UI needs to explain a surprising bounding size.
 *
 * @param worldBox the already-computed world bounding box (avoids a second walk)
 */
function computeRootTransform(
  root: any,
  worldBox: [number, number, number, number, number, number],
): RootTransformInfo {
  let rootNodeCount = 0;
  for (const scene of root.listScenes()) rootNodeCount += scene.listChildren().length;

  const local = computeLocalExtent(root);
  const world: [number, number, number] = [
    worldBox[3] - worldBox[0],
    worldBox[4] - worldBox[1],
    worldBox[5] - worldBox[2],
  ];

  // Per-axis world/local ratios (guarding against zero-size axes like a plane).
  const ratios: number[] = [];
  for (let i = 0; i < 3; i++) {
    if (local[i]! > 1e-9 && world[i]! > 1e-9) ratios.push(world[i]! / local[i]!);
  }

  const EPS = 1e-3;
  let rootUniformScale: number | undefined;
  if (ratios.length > 0) {
    const first = ratios[0]!;
    const uniform = ratios.every((r) => Math.abs(r / first - 1) < EPS);
    if (uniform) rootUniformScale = first;
  }

  // Non-identity if the world size differs from the raw vertex size by more
  // than rounding (a baked-in scale, or a non-uniform/rotated placement).
  const hasNonIdentityRoot = ratios.some((r) => Math.abs(r - 1) > EPS);

  return { rootNodeCount, rootUniformScale, hasNonIdentityRoot };
}

export async function probe(input: File | Blob, _options: ProbeOptions = {}): Promise<ModelProbeReport> {
  const buffer = await input.arrayBuffer();
  const filename = input instanceof File ? input.name : 'model.glb';
  return probeBuffer(buffer, filename);
}

export async function probeBuffer(buffer: ArrayBuffer, _filename: string): Promise<ModelProbeReport> {
  const initialSize = buffer.byteLength;
  let bailout: ModelProbeReport['bailout'] = null;
  const warnings: ModelProbeReport['warnings'] = [];

  try {
    // Parse glTF/GLB with gltf-transform
    const { doc } = await readModel(buffer);
    const root = (doc as any).getRoot();

    // Extract geometry stats
    let vertexCount = 0;
    let triangleCount = 0;
    let geometrySize = 0;
    // Key on the mesh object, not its name — distinct meshes routinely share a
    // name (or are both unnamed), and a name-keyed set undercounts meshCount.
    const meshSet = new Set<unknown>();

    for (const mesh of root.listMeshes()) {
      meshSet.add(mesh);
      for (const primitive of mesh.listPrimitives()) {
        const positions = primitive.getAttribute('POSITION');
        if (positions) {
          vertexCount += positions.getCount();
        }

        const indices = primitive.getIndices();
        if (indices) {
          triangleCount += indices.getCount() / 3;
        } else if (positions) {
          // Non-indexed primitive: every 3 consecutive POSITION vertices form a
          // triangle. Without this the mesh contributes 0 triangles, and the
          // simplification heuristics (recommendDetail / recommendErrorCeiling)
          // misclassify a dense model as "already lean".
          triangleCount += positions.getCount() / 3;
        }
      }
    }

    // Measure actual geometry data size from all accessors
    for (const acc of root.listAccessors()) {
      const bv = acc.getBuffer();
      if (bv) {
        const array = acc.getArray();
        if (array instanceof ArrayBuffer) {
          geometrySize += array.byteLength;
        } else if (array && typeof array === 'object' && 'byteLength' in array) {
          geometrySize += (array as any).byteLength;
        }
      }
    }

    // Morph-target and animation bytes: these sit in `geometrySize` but are
    // invisible in vertex counts (a baked ocean is 10K verts and 200+ targets).
    const seenAccessors = new Set<unknown>();
    const bytesOf = (acc: any): number => {
      if (!acc || seenAccessors.has(acc)) return 0;
      seenAccessors.add(acc);
      return (acc.getArray()?.byteLength as number | undefined) ?? 0;
    };
    let morphTargetCount = 0;
    let morphTargetBytes = 0;
    for (const mesh of root.listMeshes()) {
      let meshTargets = 0;
      for (const primitive of mesh.listPrimitives()) {
        const targets = primitive.listTargets();
        meshTargets = Math.max(meshTargets, targets.length);
        for (const target of targets) {
          for (const acc of target.listAttributes()) morphTargetBytes += bytesOf(acc);
        }
      }
      morphTargetCount += meshTargets;
    }

    // Extract texture stats with detailed per-texture info
    let textureCount = 0;
    let totalTextureSize = 0;
    const byFormat: Record<string, { count: number; totalSize: number }> = {};
    const textures: any[] = [];
    // Map each gltf-transform Texture to its index in `textures`, so materials
    // can be paired back to the right entry below.
    const textureIndexByRef = new Map<unknown, number>();

    for (const texture of root.listTextures()) {
      const textureIndex = textures.length;
      textureCount++;
      const image = texture.getImage();
      if (image) {
        const mimeType = texture.getMimeType() ?? 'application/octet-stream';
        const size = image.byteLength;
        const name = texture.getName() ?? `texture-${textureCount}`;
        totalTextureSize += size;

        if (!byFormat[mimeType]) {
          byFormat[mimeType] = { count: 0, totalSize: 0 };
        }
        byFormat[mimeType]!.count++;
        byFormat[mimeType]!.totalSize += size;

        // Create base64 data URL for preview
        let dataUrl: string | undefined;
        try {
          if (size < 500 * 1024) {
            // Small texture: use original as-is.
            const view = new Uint8Array(await new Blob([image]).arrayBuffer());
            dataUrl = `data:${mimeType};base64,${bytesToBase64(view)}`;
          } else {
            // Large texture: create downsampled preview
            const blob = new Blob([image]);
            const bitmap = await createImageBitmap(blob);

            // Downsample to 256px max for preview
            const maxDim = Math.max(bitmap.width, bitmap.height);
            const scale = Math.min(1, 256 / maxDim);
            const previewWidth = Math.round(bitmap.width * scale);
            const previewHeight = Math.round(bitmap.height * scale);

            // Use OffscreenCanvas to downsample
            const canvas = new OffscreenCanvas(previewWidth, previewHeight);
            const ctx = canvas.getContext('2d')!;
            ctx.drawImage(bitmap, 0, 0, previewWidth, previewHeight);

            // Encode as JPEG for size efficiency
            const previewBlob = await canvas.convertToBlob({
              type: 'image/jpeg',
              quality: 0.4, // Low quality for small preview size
            });

            const previewArray = new Uint8Array(await previewBlob.arrayBuffer());
            dataUrl = `data:image/jpeg;base64,${bytesToBase64(previewArray)}`;

            bitmap.close();
          }
        } catch {
          // Skip preview if conversion fails
        }

        // Get dimensions for GPU memory calculation, and sample the alpha
        // channel (one decode covers both).
        let width: number | undefined;
        let height: number | undefined;
        let gpuMemoryBytes: number | undefined;
        let hasAlpha: boolean | undefined;
        let alpha: AlphaAnalysis | undefined;

        try {
          const blob = new Blob([image]);
          const bitmap = await createImageBitmap(blob);
          width = bitmap.width;
          height = bitmap.height;
          // RGBA = 4 bytes per pixel (assuming worst case)
          gpuMemoryBytes = width * height * 4;

          // PNGs may carry alpha; JPEGs never do. Only analyse formats that can.
          if (mimeType === 'image/png' || mimeType === 'image/webp') {
            const result = analyzeAlpha(bitmap);
            hasAlpha = result.hasAlpha;
            alpha = result.analysis;
          } else {
            hasAlpha = false;
          }
          bitmap.close();
        } catch {
          // If we can't decode, leave dimensions/alpha undefined
        }

        textureIndexByRef.set(texture, textureIndex);
        textures.push({
          name,
          mimeType,
          sizeBytes: size,
          width,
          height,
          gpuMemoryBytes,
          dataUrl,
          hasAlpha,
          alpha,
        });
      }
    }

    // Extract material stats with per-material detail, texture pairing, hints
    let materialCount = 0;
    let pbrCount = 0;
    const materialInfos: MaterialInfo[] = [];
    for (const material of root.listMaterials()) {
      materialCount++;
      if (material.getBaseColorFactor() || material.getMetallicFactor() !== undefined || material.getRoughnessFactor() !== undefined) {
        pbrCount++;
      }

      const slots: Array<[MaterialTextureSlot, unknown]> = [
        ['baseColor', material.getBaseColorTexture()],
        ['normal', material.getNormalTexture()],
        ['metallicRoughness', material.getMetallicRoughnessTexture()],
        ['occlusion', material.getOcclusionTexture()],
        ['emissive', material.getEmissiveTexture()],
      ];
      const matTextures: MaterialInfo['textures'] = [];
      for (const [slot, tex] of slots) {
        if (!tex) continue;
        const idx = textureIndexByRef.get(tex);
        if (idx !== undefined) matTextures.push({ slot, textureIndex: idx });
      }

      const baseRef = matTextures.find((t) => t.slot === 'baseColor');
      const baseMime = baseRef ? textures[baseRef.textureIndex]?.mimeType : undefined;
      const baseColorFormat =
        baseMime === 'image/jpeg' ? 'jpeg' : baseMime === 'image/png' ? 'png' : undefined;

      // Only compute the (somewhat costly) shell test when it matters — for a
      // double-sided material whose double-sidedness we'd otherwise suggest
      // dropping.
      const openShell = material.getDoubleSided()
        ? isMaterialOpenShell(root, material)
        : undefined;

      const info: MaterialInfo = {
        name: material.getName() ?? `material-${materialCount}`,
        alphaMode: (material.getAlphaMode() as MaterialInfo['alphaMode']) ?? 'OPAQUE',
        alphaCutoff: material.getAlphaCutoff() ?? 0.5,
        doubleSided: material.getDoubleSided() ?? false,
        baseColorFactor: material.getBaseColorFactor() as [number, number, number, number],
        metallicFactor: material.getMetallicFactor() ?? 1,
        roughnessFactor: material.getRoughnessFactor() ?? 1,
        baseColorFormat,
        openShell,
        textures: matTextures,
        hints: [],
        recommended: {},
      };
      const analysis = analyzeMaterial(info, textures);
      info.hints = analysis.hints;
      info.recommended = analysis.recommended;
      materialInfos.push(info);
    }

    // Bounding box in WORLD space — node transforms (e.g. a 100× root scale on
    // Unity exports) must be applied, or the model reads at the wrong scale.
    const boundingBox = computeWorldBounds(root);
    const rootTransform = computeRootTransform(root, boundingBox);

    // Scene activation: getDefaultScene() is null when the file declares scenes
    // but omits the top-level `scene` index — the ambiguous case some runtimes
    // render as empty. The optimizer fixes this by marking the first scene active.
    const sceneInfo = {
      sceneCount: root.listScenes().length,
      hasDefaultScene: root.getDefaultScene() != null,
    };

    // Get extensions. Read them from the raw JSON (authoritative, lists every
    // declared extension); fall back to gltf-transform's registered-only view.
    const declared = readDeclaredExtensions(buffer);
    let extensionsUsed = declared.used;
    if (extensionsUsed.length === 0) {
      extensionsUsed = (root as any)
        .listExtensionsUsed()
        .map((ext: any) => ext.extensionName ?? '')
        .filter((name: string) => name.length > 0);
    }

    return {
      schemaVersion: 1,
      fileSize: initialSize,
      boundingBox,
      rootTransform,
      sceneInfo,
      geometry: {
        vertexCount,
        triangleCount,
        meshCount: meshSet.size,
        sizeBytes: geometrySize,
        morphTargetCount,
        morphTargetBytes,
      },
      textures: {
        textureCount,
        totalSize: totalTextureSize,
        byFormat,
        textures,
      },
      materials: {
        count: materialCount,
        pbrCount,
        materials: materialInfos,
      },
      extensionsUsed,
      extensionsRequired: declared.required,
      bailout,
      warnings,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    // The JSON header parses independently of the buffers, so we can usually
    // still name the extensions involved — and a required one is the most
    // likely reason the parse failed at all ("Missing required extension, …").
    const declared = readDeclaredExtensions(buffer);
    return {
      schemaVersion: 1,
      fileSize: initialSize,
      boundingBox: [0, 0, 0, 0, 0, 0],
      rootTransform: { rootNodeCount: 0, hasNonIdentityRoot: false },
      sceneInfo: { sceneCount: 0, hasDefaultScene: false },
      geometry: { vertexCount: 0, triangleCount: 0, meshCount: 0, sizeBytes: 0 },
      textures: { textureCount: 0, totalSize: 0, byFormat: {}, textures: [] },
      materials: { count: 0, pbrCount: 0, materials: [] },
      extensionsUsed: declared.used,
      extensionsRequired: declared.required,
      bailout: 'parse-error',
      warnings: [
        {
          code: 'parse-error',
          message: `Failed to parse model: ${message}`,
        },
      ],
    };
  }
}
